import { DestroyRef, Signal, WritableSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { TableLazyLoadEvent } from 'primeng/table';
import { skip } from 'rxjs';
import { PAGINATION_CONSTANTS } from '../constants/pagination.constants';
import { LazyLoadParams, PaginatedResponse } from '../types/lazy-load.interface';
import { LazyLoadConverter } from './lazy-load.converter';

export interface PaginatedListState<T> {
  currentAssetId: WritableSignal<number | null>;
  data: WritableSignal<T[]>;
  dataLoadingCompleted: WritableSignal<boolean>;
  totalRecords: WritableSignal<number>;
  first: WritableSignal<number>;
  rows: WritableSignal<number>;
}

export interface PaginatedListConfig {
  globalFilterFields: string[];
  errorKey: string;
  loadAssetById: (id: number) => Promise<void>;
  isAssetLoaded: (id: number) => boolean;
  onError?: (error: unknown) => void;
  stateKey?: string;
}

export class PaginatedListHelper<T> {
  readonly state: PaginatedListState<T> = {
    currentAssetId: signal<number | null>(null),
    data: signal<T[]>([]),
    dataLoadingCompleted: signal(false),
    totalRecords: signal(0),
    first: signal(0),
    rows: signal(PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE),
  };

  #isInitialLoad = true;
  #hasMountedOnce = false;
  #lastParams: LazyLoadParams | null = null;

  constructor(
    private readonly translateService: TranslocoService,
    readonly config: PaginatedListConfig,
  ) {}

  get lastParams(): LazyLoadParams | null {
    return this.#lastParams;
  }

  set lastParams(params: LazyLoadParams | null) {
    this.#lastParams = params;
  }

  get currentAssetId(): Signal<number | null> {
    return this.state.currentAssetId.asReadonly();
  }

  // Scopes the PrimeNG stateKey by asset so a saved filter/sort doesn't leak
  // into another ouvrage's grid when the user switches assets.
  buildStateKey(prefix: string): string | undefined {
    const assetId = this.state.currentAssetId();
    return assetId ? `${prefix}-${assetId}` : undefined;
  }

  setupRouteSubscription(route: ActivatedRoute, destroyRef: DestroyRef, onAssetChange: (shouldLoad: boolean) => Promise<void>): void {
    route.parent?.parent?.params.pipe(takeUntilDestroyed(destroyRef)).subscribe(params => {
      const id = params['id'] ? Number(params['id']) : null;

      if (id && id > 0) {
        const isFirstMount = !this.#hasMountedOnce;
        this.#hasMountedOnce = true;
        this.state.currentAssetId.set(id);
        void this.loadAssetDetails(id);
        this.#isInitialLoad = !this.config.stateKey;
        // stateKey grids rely on PrimeNG's own lazyLoadOnInit to fire the first server fetch
        // (that's what the manual load being skipped here is for) — but it only fires once per
        // component instance. The route (and this component) is reused across an in-place asset
        // switch (same route, changed :id), so a manual reload is still needed on any later one.
        void onAssetChange(!this.config.stateKey || !isFirstMount);
      } else {
        this.resetState();
      }
    });
  }

  setupLangChangeSubscription(destroyRef: DestroyRef, onLangChange: () => Promise<void>): void {
    this.translateService.langChanges$.pipe(skip(1), takeUntilDestroyed(destroyRef)).subscribe(() => {
      if (this.state.currentAssetId() && this.#lastParams) {
        void onLangChange();
      }
    });
  }

  async handleLazyLoad(event: TableLazyLoadEvent, loadFn: (params: LazyLoadParams) => Promise<void>): Promise<void> {
    if (!this.state.currentAssetId()) return;

    if (this.#isInitialLoad) {
      this.#isInitialLoad = false;
      return;
    }

    const params = LazyLoadConverter.fromTableEvent(event, this.config.globalFilterFields);
    await loadFn(params);
  }

  async executeLoad<R extends PaginatedResponse<T>>(params: LazyLoadParams, apiFn: () => Promise<R>): Promise<void> {
    if (!this.state.currentAssetId()) return;

    this.state.dataLoadingCompleted.set(false);
    this.#lastParams = params;

    try {
      const response = await apiFn();
      this.updateFromResponse(response);
    } catch (error) {
      this.state.data.set([]);
      this.state.totalRecords.set(0);
      console.error(this.translateService.translate(this.config.errorKey), error);
      this.config.onError?.(error);
    } finally {
      this.state.dataLoadingCompleted.set(true);
    }
  }

  updateFromResponse(response: PaginatedResponse<T>): void {
    this.state.data.set(response.data);
    this.state.totalRecords.set(response.total);
    this.state.first.set((response.page - 1) * response.pageSize);
    this.state.rows.set(response.pageSize);
  }

  createInitialParams(): LazyLoadParams {
    return { pagination: { page: 1, pageSize: this.state.rows() } };
  }

  reloadWithLastParams(loadFn: (params: LazyLoadParams) => Promise<void>): void {
    const params = this.#lastParams ?? this.createInitialParams();
    void loadFn(params);
  }

  private async loadAssetDetails(assetId: number): Promise<void> {
    try {
      if (!this.config.isAssetLoaded(assetId)) {
        await this.config.loadAssetById(assetId);
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.assetLoadFailed', { id: assetId }), error);
      this.resetState();
    }
  }

  private resetState(): void {
    this.state.currentAssetId.set(null);
    this.state.data.set([]);
    this.state.totalRecords.set(0);
  }
}
